import { ThemeEvents, VariantUpdateEvent } from "@theme/events";

/**
 * A custom element that displays a device protection selling plans.
 * This component listens for variant update events and updates the selling plans.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */

class DeviceProtection extends HTMLElement {
  constructor() {
    super();
    this.blockId = null;
    this.openerInput = null;
    this.mainProductForm = null;
    this.mainProductQuantitInput = null;
    this.selectedSellingPlanInput = null;
    this.closestSection = null;
    this.sellingPlanInputs = null;
  }

  connectedCallback() {
    this.initializeData();
    this.attachEvents();
  }

  disconnectedCallback() {
    const closestSection = this.closest(".shopify-section, dialog");
    if (!closestSection) return;
    closestSection.removeEventListener(
      ThemeEvents.variantUpdate,
      this.updatePlans
    );
  }

  initializeData() {
    this.blockId = this.dataset.blockId;

    const wrapper = this.closest(".device-protection__wrapper");
    if (wrapper) {
      this.openerInput = wrapper.querySelector("#device-protection__opener");
    }

    const section = this.closest(".shopify-section");
    if (section) {
      this.mainProductForm = section.querySelector("product-form-component");
    }

    if (this.mainProductForm) {
      this.mainProductQuantitInput = this.mainProductForm.querySelector(
        'input[ref="quantityInput"]'
      );
    }

    this.selectedSellingPlanInput = this.querySelector(
      'input[type="radio"]:checked'
    );

    this.closestSection = this.closest(".shopify-section, dialog");

    this.sellingPlanInputs = this.querySelectorAll('input[type="radio"]');
  }

  attachEvents() {
    // Quantity selector change trigger
    const quantitySelector = this.mainProductQuantitInput.closest(
      "quantity-selector-component"
    );

    if (quantitySelector) {
      quantitySelector.addEventListener(
        ThemeEvents.quantitySelectorUpdate,
        (event) => {
          this.mainProductForm.querySelector(
            'input[name="items[1][quantity]"]'
          ).value = event.detail.quantity;
        }
      );
    } else {
      this.mainProductQuantitInput.addEventListener("change", (event) => {
        this.mainProductForm.querySelector(
          'input[name="items[1][quantity]"]'
        ).value = event.target.value;
      });
    }

    // Opener change trigger
    this.openerInput.addEventListener(
      "change",
      this.updateMainProductForm.bind(this)
    );

    // Variant change trigger
    if (!this.closestSection) return;
    this.closestSection.addEventListener(
      ThemeEvents.variantUpdate,
      this.updatePlans.bind(this)
    );

    // Cart update trigger
    this.closestSection.addEventListener(
      ThemeEvents.cartUpdate,
      this.closeOpener.bind(this)
    );

    // Selling plan change trigger
    this.sellingPlanInputs.forEach((input) => {
      input.addEventListener("change", this.updateInputs.bind(this));
    });
  }

  /**
   * Updates the main product form.
   * @param {Event} event - The event.
   */
  updateMainProductForm(event) {
    if (event.target.checked == true) {
      if (!this.mainProductForm) return;
      this.enableAndUpdateInputs();
    } else {
      if (!this.mainProductForm) return;
      this.disableInputs();
    }
  }

  enableAndUpdateInputs() {
    this.enableInputs();
    this.updateInputs();
  }

  enableInputs() {
    const deviceProtectionInputs = this.mainProductForm.querySelectorAll(
      "input[data-device-protection-input]"
    );
    deviceProtectionInputs.forEach((input) => {
      input.disabled = false;
    });
  }

  disableInputs() {
    const deviceProtectionInputs = this.mainProductForm.querySelectorAll(
      "input[data-device-protection-input]"
    );
    deviceProtectionInputs.forEach((input) => {
      input.disabled = true;
    });
  }

  updateInputs() {
    this.selectedSellingPlanInput = this.querySelector(
      'input[type="radio"]:checked'
    );
    this.mainProductForm.querySelector('input[name="items[1][id]"]').value =
      this.selectedSellingPlanInput.dataset.variantId;

    this.mainProductForm.querySelector(
      'input[name="items[1][selling_plan]"]'
    ).value = this.selectedSellingPlanInput.value;

    const now = Date.now();

    this.mainProductForm.querySelector(
      'input[name="items[0][properties][_bundle_id]"]'
    ).value = now;

    this.mainProductForm.querySelector(
      'input[name="items[1][properties][_bundle_id]"]'
    ).value = now;
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePlans(event) {
    // Updating the device protection parent id input
    const newVariantId = event.detail.resource.id;
    this.mainProductForm.querySelector(
      'input[name="items[1][parent_id]"]'
    ).value = newVariantId;

    const newPlansBlock = event.detail.data.html.querySelector(
      `device-protection[data-block-id="${this.blockId}"]`
    );

    if (!newPlansBlock) return;

    const checkedIndex = this.querySelector('input[type="radio"]:checked')
      .dataset.index;

    // Update Plans
    const currentPlansBlock = this;
    if (newPlansBlock && currentPlansBlock)
      currentPlansBlock.replaceWith(newPlansBlock);

    // Update checked radio
    const newInputToBeChecked = newPlansBlock.querySelector(
      `input[type="radio"][data-index="${checkedIndex}"]`
    );
    if (newInputToBeChecked) newInputToBeChecked.checked = true;
  }

  //   Close the opener when the cart is updated
  closeOpener() {
    this.openerInput.checked = false;
    this.openerInput.dispatchEvent(new Event("change"));
  }
}

if (!customElements.get("device-protection")) {
  customElements.define("device-protection", DeviceProtection);
}
